/**
 * vehicles.ts — which cars are free, and how a contract is repointed at one.
 *
 * THE READ IS THE CONTRACT WIZARD'S OWN, MOVED RATHER THAN COPIED. `['available-vehicles', search]`
 * already had a query function — inside `SelectVehicleStep` — and a second surface declaring a
 * second fetcher against one key is the exact defect this project has already paid for once: two
 * shapes under `['insurance-details']`, whichever mounted first winning the cache, and an insert
 * failing on a column the other shape never selected. So the wizard imports this and the setup
 * canvas imports this, and "available" has one definition.
 *
 * WHAT "AVAILABLE" MEANS, UNCHANGED: a vehicle carrying no contract whose latest
 * `contract_updates` status is anything other than TERMINATED. A car on a DRAFT contract is
 * therefore NOT available — including the car on the contract being edited, which is why the
 * assignment row shows the current vehicle on its trigger rather than expecting to find it in the
 * list.
 */

import { supabase } from '@/integrations/supabase/client';

/** The row both the wizard and the setup picker hold. */
export interface AvailableVehicle {
    id: number;
    make: string;
    model: string;
    vin?: string;
    registration_number?: string;
}

export const AVAILABLE_VEHICLES_KEY = ['available-vehicles'] as const;

export const availableVehiclesKey = (search: string) =>
    [...AVAILABLE_VEHICLES_KEY, search] as const;

/** One contract's hold on a car, with the status history the hold is decided from. */
interface VehicleHold {
    vehicle_id: number;
    contract_updates: { status: string; created_at: string }[] | null;
}

/**
 * The plate embed is a to-one through `vehicles.registration_id`, so PostgREST returns an object
 * rather than the array a reverse embed would give. Read the memory note before "fixing" this:
 * plates link `vehicles.registration_id → vehicle_registrations.id`, never the other way.
 */
interface VehicleCandidate {
    id: number;
    make: string;
    model: string;
    vin: string | null;
    vehicle_registrations: { number: string } | null;
}

export async function fetchAvailableVehicles(search: string): Promise<AvailableVehicle[]> {
    // Every contract that holds a vehicle, with its status history.
    const { data: allContracts } = await supabase
        .from('contracts')
        .select('vehicle_id, contract_updates(status, created_at)')
        .not('vehicle_id', 'is', null);

    // vehicle_id → latest contract status
    const latestStatusMap = new Map<number, string>();
    (allContracts as unknown as VehicleHold[] | null)?.forEach((contract) => {
        const updates = contract.contract_updates || [];
        if (updates.length === 0) return;
        const sorted = [...updates].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );
        const latestStatus = sorted[0]?.status;
        const existing = latestStatusMap.get(contract.vehicle_id);
        // A vehicle already held by a non-terminated contract stays held.
        if (existing && !['TERMINATED'].includes(existing)) return;
        latestStatusMap.set(contract.vehicle_id, latestStatus);
    });

    const unavailableVehicleIds: number[] = [];
    latestStatusMap.forEach((status, vehicleId) => {
        if (status !== 'TERMINATED') unavailableVehicleIds.push(vehicleId);
    });

    let query = supabase
        .from('vehicles')
        .select('id, make, model, vin, vehicle_registrations(number)')
        .order('created_at', { ascending: false })
        .limit(50);

    if (unavailableVehicleIds.length > 0) {
        query = query.not('id', 'in', `(${unavailableVehicleIds.join(',')})`);
    }

    if (search) {
        // The plate lives on a joined row, so matching ids are resolved first and folded into the
        // same `or` as the columns that sit on `vehicles` itself.
        const { data: regMatches } = await supabase
            .from('vehicles')
            .select('id, vehicle_registrations!inner(number)')
            .ilike('vehicle_registrations.number', `%${search}%`);
        const regMatchIds = regMatches?.map((vehicle) => vehicle.id) || [];

        if (regMatchIds.length > 0) {
            query = query.or(
                `make.ilike.%${search}%,model.ilike.%${search}%,vin.ilike.%${search}%,id.in.(${regMatchIds.join(',')})`,
            );
        } else {
            query = query.or(
                `make.ilike.%${search}%,model.ilike.%${search}%,vin.ilike.%${search}%`,
            );
        }
    }

    const { data, error } = await query;
    if (error) throw error;

    return (
        (data as unknown as VehicleCandidate[] | null)?.map((vehicle) => ({
            id: vehicle.id,
            make: vehicle.make,
            model: vehicle.model,
            vin: vehicle.vin ?? undefined,
            registration_number: vehicle.vehicle_registrations?.number,
        })) || []
    );
}

/**
 * Repoint the contract at another car.
 *
 * ONE WRITE, TWO COLUMNS, AND THE SECOND IS NOT OPTIONAL. `contracts.vehicle_mileage` holds a
 * reading taken off the car being replaced, so a change that left it standing would file the
 * previous car's odometer as this contract's handover baseline — the figure a later argument about
 * wear, mileage or valuation is settled against — and `get_preparation_status` would go on calling
 * the mileage item complete. Clearing it in the same statement is what makes the gate reopen with
 * the assignment rather than a refetch later.
 */
export async function changeContractVehicle({
    contractId,
    vehicleId,
}: {
    contractId: number;
    vehicleId: number;
}): Promise<void> {
    const { error } = await supabase
        .from('contracts')
        .update({ vehicle_id: vehicleId, vehicle_mileage: null })
        .eq('id', contractId);
    if (error) throw error;
}
